/**
 * Provider accounts: the durable vocabulary shared by the store, the brain runtime and the browser.
 *
 * A brain provider is a LOGICAL identity (its id is what `provider/model` references, grants, role pins and
 * workflow pins name). The accounts it can run on are its CONNECTIONS: each has a globally unique immutable
 * id, its own label and its own on/off switch. The accounts of one provider are an interchangeable pool, so who
 * may run on them is the provider/model grant alone and nothing else in the system is allowed to decide which
 * of them serves a request — that is the routing selector's job alone.
 *
 * Pure data and edit projections, no imports: the store codec validates the shape and the web may type-import it
 * without pulling daemon runtime code into the browser. The conversation's account line is a WIRE shape and
 * lives in the wire contract, which both toolchains already import.
 */

/** The stored logical provider kinds, shared by configuration and prepared runtimes. */
export type BrainProviderType = 'openai' | 'anthropic' | 'oauth-anthropic' | 'oauth-github-copilot' | 'oauth-openai-codex' | 'oauth-kimi' | 'oauth-xai' | 'oauth-meta' | 'oauth-opendesign' | 'oauth-openrouter' | 'router';

/** One account of a logical provider, without its secret. */
export interface BrainProviderConnection {
  /** Globally unique and immutable: stored model references, routing affinity and usage rows name it. */
  id: string;
  /** Display name; renaming it moves no id and touches no history. */
  label: string;
  /** The account's on/off switch. `false` pauses it: the credential, its settings and its history survive, its
   *  maintenance refresh keeps running, but no new model request is dispatched to it, retries, compaction and
   *  background work included. A request already in flight may finish. */
  enabled: boolean;
  /** Credential generation: bumped whenever a login replaces the account's identity, so a runtime, a cached
   *  catalog or a stored route bound to the previous login is recognised as stale. */
  generation: number;
  /** The account's monthly spend limit in whole micro-USD (the bounds of every spend limit), or null for none.
   *  Counted per UTC calendar month from the list-price counter of the requests this account served; an
   *  account at its limit is ineligible (`spend-limit`) until the month turns. */
  spendLimitMicrousd: number | null;
}

/** The account as any API response carries it: no secret, only whether one is configured. */
export interface BrainProviderConnectionView extends BrainProviderConnection { apiKeySet: boolean }

/** The typed routing policy of one logical provider. The migrated default is routing OFF, i.e. every
 *  conversation stays on the provider's ONE selected account and never moves on its own. */
export interface ProviderRoutingConfig {
  /** Off: the NAMED default account serves every conversation, and nothing switches or fails over on its own.
   *  A provider with no usable default has no account to run on, which is an explicit unavailable state rather
   *  than an invitation to pick one — and it is not a second resolution path: the same selector validates the
   *  default (enabled, the caller's provider grant, authentication, the model's availability) and either
   *  returns it or reports unavailability.
   *  On: the selector picks by consumption, keeps an active conversation's account until the idle threshold,
   *  and fails over to another ELIGIBLE account when one is exhausted. */
  enabled: boolean;
  /** The account routing-off uses, and one candidate among the others while routing is on — never a privileged
   *  one. It passes exactly the same eligibility checks as any other account, so a default that is paused, not
   *  authorized for the caller or out of quota makes the provider unavailable rather than routing around it.
   *  The migrated default is the single account a converted provider has, chosen explicitly. */
  defaultConnectionId: string | null;
  /** Operator switch for VOLUNTARY idle rebalancing, separate from `enabled`: with routing on, an active
   *  conversation may move to a less-used account only after the idle threshold AND only when this is on.
   *  Forced moves (an exhausted, revoked or disconnected account) are not rebalancing and ignore it. */
  idleRebalanceEnabled: boolean;
  /** Minutes of conversation inactivity after which a voluntary rebalance may move it to a better account.
   *  Measured from the last real model request; a long-running tool or stream is not an idle conversation. */
  idleRebalanceMinutes: number;
  /** Smallest improvement, in percentage points of the busiest valid window, that justifies a voluntary move.
   *  Stops the selector from chasing noise in the reported figures. */
  minGainPercentPoints: number;
}

/** A logical cross-provider router owns no accounts or transport settings. */
export interface BrainRouterConfig {
  id: string;
  label: string;
  type: 'router';
  connectionIds: string[];
  /** Empty means use the common catalog; projected model lists are always explicit. */
  models: string[];
  connections: [];
  routing: ProviderRoutingConfig;
  baseUrl?: never;
  api?: never;
  compatibility?: never;
  temperature?: never;
  account?: never;
  hostedToolSearchEnabled?: never;
  codeModeEnabled?: never;
  responsesStatefulEnabled?: never;
}

/** The migrated default: routing off, idle rebalancing on (it only ever applies while routing is on),
 *  rebalance after an hour, and never for a marginal gain. */
export const PROVIDER_ROUTING_DEFAULTS: ProviderRoutingConfig = {
  enabled: false, defaultConnectionId: null, idleRebalanceEnabled: true, idleRebalanceMinutes: 60, minGainPercentPoints: 10,
};

/** One provider account's spend this UTC month against its own limit.
 *  @externalConsumer Byte-pinned browser mirror carries the daemon's spend shape. */
export interface ConnectionSpend {
  spentMicrousd: number;
  /** Null when the account has no limit. */
  limitMicrousd: number | null;
  /** ISO instant the month turns and the spend starts again from zero. */
  resetsAt: string;
}

/** A config PUT account: an absent key preserves its secret; null explicitly erases it.
 *  Setup only writes strings, while the settings editor can also clear a key. */
export type ConnectionPatch<ApiKey extends string | null = string | null> =
  BrainProviderConnection & { apiKey?: ApiKey };

/** Project the caller's public provider shape without duplicating its optional settings vocabulary. */
export type ProviderEdit<Provider, ApiKey extends string | null = string | null> =
  Provider extends { type: 'router' } ? Provider : Provider extends unknown
    ? Omit<Provider, 'connections'> & { connections: ConnectionPatch<ApiKey>[] }
    : never;

/** Strip only the read-only credential flag. Copy the provider and its accounts, not nested settings. */
export function stripProvider<
  Provider extends { connections: BrainProviderConnectionView[] },
  ApiKey extends string | null = string | null,
>(provider: Provider): ProviderEdit<Provider, ApiKey> {
  const { connections, ...entry } = provider;
  return {
    ...entry,
    connections: connections.map(({ apiKeySet: _apiKeySet, ...connection }) => connection),
  } as ProviderEdit<Provider, ApiKey>;
}

/** Prepare a wholesale list save. Setup merges existing settings first; the web replaces the entry.
 *  Both preserve list order, append new ids and leave inputs untouched. */
export function upsertProvider<Provider extends { id: string; connections: BrainProviderConnectionView[] }>(
  providers: readonly Provider[],
  entry: ProviderEdit<Provider>,
  mode: 'merge' | 'replace',
): ProviderEdit<Provider>[] {
  const persisted = providers.map((provider) => stripProvider(provider));
  const existing = persisted.find((provider) => provider.id === entry.id);
  const updated = mode === 'merge' ? { ...existing, ...entry } : entry;
  return existing
    ? persisted.map((provider) => provider.id === entry.id ? updated : provider)
    : [...persisted, updated];
}

/** Every account id in use across the whole list, so a new one is unique instance-wide. */
export function takenConnectionIds(providers: readonly { connections: readonly { id: string }[] }[]): Set<string> {
  return new Set(providers.flatMap((provider) => provider.connections.map((connection) => connection.id)));
}
